import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import GruposSecuenciales from './components/GruposSecuenciales';
import WayaCredencialesCard from './components/WayaCredencialesCard';
import { Settings, Play, Eye, Save, RefreshCw, Clock, CheckCircle2, AlertTriangle, Loader2, ShieldAlert, Zap, History, Info, Layers, ListChecks, X } from 'lucide-react';

const DIAS_SEMANA = [
  { v: 0, l: 'Lunes' }, { v: 1, l: 'Martes' }, { v: 2, l: 'Miércoles' },
  { v: 3, l: 'Jueves' }, { v: 4, l: 'Viernes' }, { v: 5, l: 'Sábado' }, { v: 6, l: 'Domingo' },
];

const fmtMoney = (v) => (v == null ? '—' : new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }).format(v));
const fmtNum = (v) => (v == null ? '—' : new Intl.NumberFormat('es-AR').format(v));
const fmtVal = (v, tipo) => (tipo === 'numero' ? fmtNum(v) : tipo === 'texto' ? (v == null ? '—' : String(v)) : fmtMoney(v));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' }) : '—');

const freqLabel = (a) => {
  if (a.cron_plataforma) return a.cron_plataforma;
  if (a.frecuencia === 'bonda_email') return 'Cada 2 hs · 9 a 18 hs · 1 vez por mes';
  if (a.frecuencia === 'manual') return 'Manual';
  if (a.frecuencia === 'diaria') return `Diaria · ${a.hora}`;
  if (a.frecuencia === 'semanal') return `Semanal · ${(DIAS_SEMANA.find((d) => d.v === a.dia_semana) || {}).l} ${a.hora}`;
  if (a.frecuencia === 'mensual') return `Mensual · ${a.dia_mes === 'ultimo' ? 'último día' : 'primer día'} ${a.hora}`;
  return a.frecuencia;
};

const EstadoBadge = ({ status }) => {
  if (status === 'ok') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 size={12} className="mr-1" />Última: OK</Badge>;
  if (status === 'error') return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><AlertTriangle size={12} className="mr-1" />Última: Error</Badge>;
  if (status === 'en_ejecucion') return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100"><Loader2 size={12} className="mr-1 animate-spin" />Ejecutando…</Badge>;
  if (status === 'sin_datos') return <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100"><Info size={12} className="mr-1" />Revisado · sin novedades</Badge>;
  return <Badge variant="secondary">Sin ejecutar</Badge>;
};

const AutomatizacionCard = ({ a, onChange, onSave, onEjecutar, onPreview, saving, running, selMode, selected, onToggleSelect }) => {
  const set = (k, v) => onChange(a.id, k, v);
  const isBonda = a.frecuencia === 'bonda_email';
  const isFija = isBonda || !!a.cron_plataforma;
  const enGrupo = !!a.grupo_programado;
  const [showHist, setShowHist] = useState(false);
  return (
    <Card className="p-5 lg:p-6" data-testid={`auto-card-${a.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {selMode && (
            <div className="pt-1 shrink-0">
              <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(a.id)} data-testid={`auto-select-${a.id}`} />
            </div>
          )}
          <div className="p-2 rounded-lg bg-indigo-100 shrink-0"><Zap size={22} className="text-indigo-600" /></div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">{a.nombre}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{a.descripcion}</p>
            <p className="text-xs text-slate-400 mt-1">Fuente: {a.fuente}</p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <EstadoBadge status={a.last_status} />
              {enGrupo && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100"><Layers size={11} className="mr-1" />En grupo: {a.grupo_programado.nombre}</Badge>}
              <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> Última: {fmtDate(a.last_run)}</span>
              <span className="text-xs text-slate-500">· Próxima: {fmtDate(a.next_run)}</span>
            </div>
            {a.last_result && (a.last_result.valor != null || a.last_result.monto != null) && (
              <p className="text-xs text-emerald-700 mt-1" data-testid={`auto-last-result-${a.id}`}>
                Último dato: <span className="font-semibold">{fmtVal(a.last_result.valor != null ? a.last_result.valor : a.last_result.monto, a.last_result.valor_tipo || 'money')}</span> · período {a.last_result.periodo}
              </p>
            )}
            {a.last_status === 'sin_datos' && a.last_skip && a.last_skip.resumen && (
              <p className="text-xs text-slate-500 mt-1 flex items-start gap-1" data-testid={`auto-skip-note-${a.id}`}>
                <Info size={12} className="mt-0.5 shrink-0 text-slate-400" /> {a.last_skip.resumen}
              </p>
            )}
            {a.last_status === 'error' && a.last_error && (
              <p className="text-xs text-rose-600 mt-1">Error: {a.last_error}</p>
            )}
            {(a.historial || []).length > 0 && (
              <div className="mt-3">
                <button type="button" onClick={() => setShowHist((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors" data-testid={`auto-hist-toggle-${a.id}`}>
                  <History size={13} /> {showHist ? 'Ocultar historial' : `Ver historial (${(a.historial || []).length})`}
                </button>
                {showHist && (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 divide-y divide-slate-100 max-h-60 overflow-auto" data-testid={`auto-hist-${a.id}`}>
                    {(a.historial || []).slice().reverse().map((h, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`auto-hist-row-${a.id}-${i}`}>
                        <span className="text-xs text-slate-500 shrink-0 flex items-center gap-1"><Clock size={11} /> {fmtDate(h.ts)}</span>
                        <span className="text-xs text-slate-700 flex-1 truncate text-right">
                          {h.status === 'ok' ? (
                            <>
                              {h.valor != null && <span className="font-semibold">{fmtVal(h.valor, h.valor_tipo || 'money')}</span>}
                              {h.periodo && <span className="text-slate-400"> · {h.periodo}</span>}
                              {h.valor == null && h.resumen && <span className="text-slate-500">{h.resumen}</span>}
                            </>
                          ) : (
                            <span className="text-rose-600 truncate">{h.error || 'Error'}</span>
                          )}
                        </span>
                        {h.status === 'ok' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 shrink-0"><CheckCircle2 size={11} className="mr-1" />OK</Badge>
                        ) : (
                          <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 shrink-0"><AlertTriangle size={11} className="mr-1" />Error</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 lg:w-[420px] bg-slate-50 rounded-lg p-4 border border-slate-100">
          {enGrupo ? (
            <div className="rounded-md bg-indigo-50 border border-indigo-100 p-3 mb-3" data-testid={`auto-grupo-info-${a.id}`}>
              <Label className="text-xs text-indigo-700 flex items-center gap-1"><Layers size={12} /> Programado por grupo</Label>
              <p className="text-sm font-medium text-slate-800 mt-0.5">{a.grupo_programado.nombre}</p>
              <p className="text-xs text-slate-500 mt-1">Corre dentro de este grupo, en secuencia. Su horario individual está suspendido para no duplicar corridas.</p>
            </div>
          ) : (
          <>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium text-slate-700">Activa</Label>
            <Switch checked={!!a.activa} onCheckedChange={(v) => set('activa', v)} data-testid={`auto-activa-${a.id}`} />
          </div>
          {isFija ? (
            <div className="rounded-md bg-white border border-slate-200 p-3" data-testid={`auto-fixed-sched-${a.id}`}>
              <Label className="text-xs text-slate-600">Agenda</Label>
              {isBonda ? (
                <>
                  <p className="text-sm font-medium text-slate-800 mt-0.5">Cada 2 hs · 9 a 18 hs</p>
                  <p className="text-xs text-slate-500 mt-1">Arranca el 1ro de cada mes y revisa la casilla hasta encontrar el mail. Una vez procesado, no vuelve a revisar hasta el mes siguiente.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-slate-800 mt-0.5">{a.cron_plataforma}</p>
                  <p className="text-xs text-slate-500 mt-1">Programada por la plataforma. Con el switch «Activa» la podés pausar; cada corrida queda registrada en el historial.</p>
                </>
              )}
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Frecuencia</Label>
              <Select value={a.frecuencia} onValueChange={(v) => set('frecuencia', v)}>
                <SelectTrigger className="mt-1 h-9" data-testid={`auto-frecuencia-${a.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual (sin agenda)</SelectItem>
                  <SelectItem value="diaria">Diaria</SelectItem>
                  <SelectItem value="semanal">Semanal</SelectItem>
                  <SelectItem value="mensual">Mensual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {a.frecuencia === 'mensual' && (
              <div>
                <Label className="text-xs text-slate-600">Día del mes</Label>
                <Select value={a.dia_mes} onValueChange={(v) => set('dia_mes', v)}>
                  <SelectTrigger className="mt-1 h-9" data-testid={`auto-diames-${a.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primero">Primer día del mes</SelectItem>
                    <SelectItem value="ultimo">Último día del mes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {a.frecuencia === 'semanal' && (
              <div>
                <Label className="text-xs text-slate-600">Día de la semana</Label>
                <Select value={String(a.dia_semana)} onValueChange={(v) => set('dia_semana', parseInt(v, 10))}>
                  <SelectTrigger className="mt-1 h-9" data-testid={`auto-diasemana-${a.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIAS_SEMANA.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {a.frecuencia !== 'manual' && (
              <div>
                <Label className="text-xs text-slate-600">Hora</Label>
                <Input type="time" value={a.hora} onChange={(e) => set('hora', e.target.value)} className="mt-1 h-9" data-testid={`auto-hora-${a.id}`} />
              </div>
            )}
          </div>
          )}
          {!isFija && <p className="text-xs text-slate-400 mt-2">{freqLabel(a)}</p>}
          </>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            {!enGrupo && (
            <Button size="sm" onClick={() => onSave(a)} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`auto-guardar-${a.id}`}>
              {saving ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Save size={15} className="mr-1.5" />} Guardar
            </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onEjecutar(a)} disabled={running} data-testid={`auto-ejecutar-${a.id}`}>
              {running ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Play size={15} className="mr-1.5" />} Ejecutar ahora
            </Button>
            {a.soporta_preview && (
              <Button size="sm" variant="ghost" onClick={() => onPreview(a)} disabled={running} className="text-slate-600" data-testid={`auto-preview-${a.id}`}>
                <Eye size={15} className="mr-1.5" /> Preview
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const KpisConfiguracion = () => {
  const [autos, setAutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selMode, setSelMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [presetMembers, setPresetMembers] = useState(null);
  const [runningSel, setRunningSel] = useState(false);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/kpis/automatizaciones');
      setAutos(r.data.automatizaciones || []);
      return r.data.automatizaciones || [];
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else if (!silent) toast.error('Error al cargar la configuración');
    } finally {
      if (!silent) setLoading(false);
    }
    return [];
  };
  useEffect(() => { load(); }, []);

  const onChange = (id, key, val) => setAutos((prev) => prev.map((a) => (a.id === id ? { ...a, [key]: val } : a)));

  const onSave = async (a) => {
    setSavingId(a.id);
    try {
      const r = await api.put(`/kpis/automatizaciones/${a.id}`, {
        frecuencia: a.frecuencia, dia_mes: a.dia_mes, dia_semana: a.dia_semana, hora: a.hora, activa: a.activa,
      });
      setAutos((prev) => prev.map((x) => (x.id === a.id ? r.data : x)));
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingId(null);
    }
  };

  const pollStatus = (id, tries) => {
    if (tries > 40) { setRunningId(null); return; }
    setTimeout(async () => {
      const list = await load(true);
      const item = (list || []).find((x) => x.id === id);
      if (item && item.last_status === 'en_ejecucion') pollStatus(id, tries + 1);
      else setRunningId(null);
    }, 6000);
  };

  const onEjecutar = async (a) => {
    setRunningId(a.id);
    try {
      await api.post(`/kpis/automatizaciones/${a.id}/ejecutar`);
      toast.success('Ejecución iniciada. Según la fuente puede tardar hasta un par de minutos…');
      setAutos((prev) => prev.map((x) => (x.id === a.id ? { ...x, last_status: 'en_ejecucion' } : x)));
      pollStatus(a.id, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar');
      setRunningId(null);
    }
  };

  const onPreview = async (a) => {
    setPreviewLoading(true);
    setPreview({ id: a.id, nombre: a.nombre, loading: true });
    try {
      const r = await api.post(`/kpis/automatizaciones/${a.id}/preview`);
      setPreview({ id: a.id, nombre: a.nombre, detalle: r.data.detalle });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al obtener el preview');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const cancelSel = () => { setSelMode(false); setSelected([]); };

  const pollSelection = (ids, tries) => {
    if (tries > 60) { setRunningSel(false); return; }
    setTimeout(async () => {
      const list = await load(true);
      const stillRunning = (list || []).some((x) => ids.includes(x.id) && x.last_status === 'en_ejecucion');
      if (stillRunning) pollSelection(ids, tries + 1);
      else setRunningSel(false);
    }, 5000);
  };

  const onEjecutarSeleccion = async () => {
    if (!selected.length) return;
    setRunningSel(true);
    const ids = [...selected];
    try {
      await api.post('/kpis/automatizaciones/ejecutar-seleccion', { miembros: ids });
      toast.success(`Ejecutando ${ids.length} automatizaciones en secuencia…`);
      setAutos((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, last_status: 'en_ejecucion' } : x)));
      cancelSel();
      pollSelection(ids, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar la selección');
      setRunningSel(false);
    }
  };

  const guardarSeleccionComoGrupo = () => {
    if (!selected.length) return;
    setPresetMembers([...selected]);
    cancelSel();
  };

  if (denied) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="kpis-config-denied">
        <ShieldAlert size={40} className="mx-auto text-amber-500 mb-3" />
        <h2 className="text-lg font-semibold text-slate-800">Acceso restringido</h2>
        <p className="text-sm text-slate-500 mt-1">La Configuración de KPIs está disponible solo para administradores.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 lg:p-6" data-testid="kpis-configuracion">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-slate-900 shrink-0"><Settings size={24} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Configuración</h1>
            <p className="text-sm text-slate-500 mt-1">Automatizaciones de extracción de datos para los KPIs. Configurá la frecuencia o ejecutá cada una al momento.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => (selMode ? cancelSel() : setSelMode(true))} data-testid="kpis-config-selmode" className={selMode ? 'border-indigo-300 text-indigo-700 bg-indigo-50' : ''}>
            <ListChecks size={15} className="mr-1.5" /> {selMode ? 'Cancelar selección' : 'Seleccionar'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="kpis-config-refresh">
            <RefreshCw size={15} className="mr-1.5" /> Actualizar
          </Button>
        </div>
      </div>

      <WayaCredencialesCard />

      <GruposSecuenciales
        autos={autos}
        onRefresh={() => load(true)}
        presetMembers={presetMembers}
        onPresetConsumed={() => setPresetMembers(null)}
      />

      {selMode && (
        <div className="mb-3 rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-2.5 text-sm text-indigo-700 flex items-center gap-2" data-testid="seleccion-hint">
          <ListChecks size={15} /> Tildá las automatizaciones que quieras correr en secuencia (una tras otra) o guardá como grupo.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" /> Cargando…</div>
      ) : autos.length === 0 ? (
        <Card className="p-10 text-center text-slate-500">Todavía no hay automatizaciones configuradas.</Card>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Settings size={18} className="text-slate-500" /> Automatizaciones individuales</h2>
          {autos.map((a) => (
            <AutomatizacionCard
              key={a.id} a={a} onChange={onChange} onSave={onSave}
              onEjecutar={onEjecutar} onPreview={onPreview}
              saving={savingId === a.id} running={runningId === a.id}
              selMode={selMode} selected={selected.includes(a.id)} onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="auto-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview — {preview?.nombre}</DialogTitle>
          </DialogHeader>
          {previewLoading || preview?.loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="animate-spin mr-2" /> Consultando la fuente… (puede tardar ~30s)</div>
          ) : preview?.detalle ? (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 uppercase tracking-wide">{preview.detalle.campo || 'Valor extraído'}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="auto-preview-monto">{fmtVal(preview.detalle.valor != null ? preview.detalle.valor : preview.detalle.monto, preview.detalle.valor_tipo || 'money')}</p>
                <p className="text-xs text-slate-400 mt-1">Período: {preview.detalle.periodo}</p>
              </div>
              <div className="text-sm text-slate-600 space-y-1">
                {(preview.detalle.metricas || []).map((m, i) => (
                  <div key={i} className="flex justify-between" data-testid={`auto-preview-metrica-${i}`}>
                    <span className="text-slate-500">{m.label}</span><span>{fmtVal(m.valor, m.tipo)}</span>
                  </div>
                ))}
                {preview.detalle.desde && preview.detalle.hasta && (
                  <div className="flex justify-between"><span className="text-slate-500">Rango consultado</span><span>{preview.detalle.desde} → {preview.detalle.hasta}</span></div>
                )}
                {preview.detalle.raw != null && (
                  <div className="flex justify-between"><span className="text-slate-500">Valor crudo</span><span className="font-mono text-xs">{preview.detalle.raw}</span></div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">Fuente</span><span className="text-right">{preview.detalle.fuente}</span></div>
              </div>
              <p className="text-xs text-slate-400 pt-2 border-t">Preview no guarda el dato. Usá «Ejecutar ahora» para actualizar el KPI.</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {selMode && selected.length > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex flex-wrap items-center justify-center gap-3 bg-slate-900 text-white rounded-2xl shadow-xl px-5 py-3" data-testid="seleccion-barra">
          <span className="text-sm font-medium" data-testid="seleccion-count">{selected.length} seleccionada{selected.length > 1 ? 's' : ''}</span>
          <Button size="sm" onClick={onEjecutarSeleccion} disabled={runningSel} className="bg-white text-slate-900 hover:bg-slate-100" data-testid="seleccion-ejecutar">
            {runningSel ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Play size={15} className="mr-1.5" />} Ejecutar en secuencia
          </Button>
          <Button size="sm" variant="outline" onClick={guardarSeleccionComoGrupo} className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="seleccion-guardar-grupo">
            <Layers size={15} className="mr-1.5" /> Guardar como grupo
          </Button>
          <button onClick={cancelSel} className="text-white/70 hover:text-white" data-testid="seleccion-cerrar"><X size={18} /></button>
        </div>
      )}
    </div>
  );
};

export default KpisConfiguracion;
